import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHref } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import { useRecordMedicationHistoryConsentMutation } from '@features/eprescriptions/store/api';
import {
  useMedicationsQuery,
  useRemoveMedicationMutation,
  useUpdateMedicationMutation,
} from '@features/patients/store/api';
import { useSubjectLanguageVariants } from '@features/user/store/hooks';
import { Box, Skeleton, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useModal } from '@store/hooks/useModal';
import { useSnackbar } from 'notistack';

import { ClinicalCardFrame } from '../components/ClinicalCardFrame';
import { MedicationsCard } from '../components/MedicationsCard';
import { useUnifiedMedicationsQuery } from '../store/api';
import { mapUnifiedToVM } from '../vm/mapUnifiedToVM';

interface V1MedicationsCardProps {
  patientId: string;
  /**
   * Fire the cardOpened impression on mount (default). Hosts that embed this
   * card inside another tracked surface (the context-bar edit modal, which
   * fires editModalOpened) pass false so one open isn't counted twice.
   */
  trackImpression?: boolean;
}

/** Poll cadence while a post-consent SureScripts pull is in flight. */
const PULLING_POLL_MS = 3000;

/**
 * Data glue for the unified Medications card on the V1 patient page
 * (PatientDetailView). Reads Component D's unified history, writes through the
 * existing per-resource medication CRUD (add/edit via the shared
 * AddMedicationModal), and drives the consent-gated SureScripts section.
 *
 * Write contract (e-plan §3.6): discontinue sends `{ is_active: false }` — the
 * backend silently DISCARDS a bare `status` string; Remove is the soft DELETE
 * (the row survives server-side as inactive_reason='removed' and the mapper
 * filters it out of the view).
 */
export const V1MedicationsCard: React.FC<V1MedicationsCardProps> = ({
  patientId,
  trackImpression = true,
}) => {
  const [pollMs, setPollMs] = useState(0);
  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useUnifiedMedicationsQuery({ patientId }, { pollingInterval: pollMs });
  // The v1 spine list backs the edit modal's field set (form/route/timing…),
  // which the unified projection doesn't carry. Same cache family; cheap.
  // The card waits for it: AddMedicationModal seeds react-hook-form from
  // defaultValues once at mount, so opening Edit before the spine resolves
  // would show a blank form whose save blanks real fields.
  const { data: spineData, isLoading: isSpineLoading } = useMedicationsQuery({
    patientId,
  });

  const [updateMedication] = useUpdateMedicationMutation();
  const [removeMedication] = useRemoveMedicationMutation();
  const [recordConsent] = useRecordMedicationHistoryConsentMutation();
  const { open } = useModal();
  const { enqueueSnackbar } = useSnackbar();
  const { unifiedMedications: tracking, medicationHistory: consentTracking } =
    useEventTracking();
  const { singular: subject } = useSubjectLanguageVariants();
  // The prescribe launcher validates the patient, mints the DoseSpot SSO URL,
  // and redirects — opened in a new tab so the chart stays put.
  const prescribeHref = useHref(
    `/jotrx/prescribe-launch/${patientId}?source=unified_meds_card`,
  );

  const cardData = useMemo(() => (data ? mapUnifiedToVM(data) : undefined), [data]);

  // Poll while the post-consent pull is in flight so the section flips to
  // granted without a reload (mirrors the shipped V2 section's behavior).
  // Read via the mapper so the missing-consent fallback lives in one place.
  const consentState = cardData?.consentState;
  useEffect(() => {
    setPollMs(consentState === 'pulling' ? PULLING_POLL_MS : 0);
  }, [consentState]);

  // One impression per mount, once the unified data is in.
  const openedTracked = useRef(false);
  useEffect(() => {
    if (!trackImpression || openedTracked.current || !cardData || !consentState) return;
    openedTracked.current = true;
    tracking.cardOpened({
      patient_id: patientId,
      medication_count: cardData.workspace.episodes.length,
      history_fill_count: cardData.historyFills.length,
      consent_state: consentState,
    });
  }, [cardData, consentState, patientId, tracking, trackImpression]);

  const spineById = useMemo(
    () => new Map((spineData?.medications ?? []).map((med) => [med.medication_id, med])),
    [spineData],
  );

  const handleAdd = useCallback(() => {
    tracking.addClicked({ patient_id: patientId });
    open(MODAL_TYPES.ADD_MEDICATION, { patientId });
  }, [open, patientId, tracking]);

  const handleEdit = useCallback(
    (medicationId: string) => {
      tracking.editClicked({ patient_id: patientId, medication_id: medicationId });
      const med = spineById.get(medicationId);
      if (!med) {
        enqueueSnackbar(
          'Medication details are still loading — try again in a moment.',
          { variant: 'info' },
        );
        return;
      }
      open(MODAL_TYPES.ADD_MEDICATION, {
        patientId,
        medicationId,
        defaultValues: {
          name: med.name,
          generic_name: med.generic_name ?? '',
          strength: med.strength ?? '',
          form: med.form ?? '',
          route: med.route ?? '',
          dosage: med.dosage ?? '',
          frequency: med.frequency ?? '',
          timing: med.timing ?? '',
          start_date: med.start_date ?? '',
          end_date: med.end_date ?? '',
          indication: med.indication ?? '',
          is_active: med.is_active ?? true,
        },
      });
    },
    [enqueueSnackbar, open, patientId, spineById, tracking],
  );

  const handleDiscontinue = useCallback(
    (medicationId: string) => {
      tracking.discontinueClicked({ patient_id: patientId, medication_id: medicationId });
      void updateMedication({
        patientId,
        medicationId,
        updateMedicationRequest: { is_active: false },
      })
        .unwrap()
        .catch(() => {
          enqueueSnackbar('Could not discontinue the medication. Please try again.', {
            variant: 'error',
          });
        });
    },
    [updateMedication, patientId, enqueueSnackbar, tracking],
  );

  const handleRemove = useCallback(
    (medicationId: string) => {
      tracking.removeClicked({ patient_id: patientId, medication_id: medicationId });
      void removeMedication({ patientId, medicationId })
        .unwrap()
        .catch(() => {
          enqueueSnackbar('Could not remove the medication. Please try again.', {
            variant: 'error',
          });
        });
    },
    [removeMedication, patientId, enqueueSnackbar, tracking],
  );

  const handleRecordConsent = useCallback(() => {
    consentTracking.consentClicked({ patient_id: patientId });
    void recordConsent({ patientId })
      .unwrap()
      .then(() => {
        consentTracking.consentSubmitted({ patient_id: patientId });
        // The consent mutation lives in the eprescriptions slice and doesn't
        // know this feature's UnifiedMedications tag, so kick the unified
        // query directly: the refetch observes 'pulling', which turns the
        // poll on until the SureScripts pull lands and the section flips to
        // granted — no page reload.
        void refetch();
      })
      .catch(() => {
        enqueueSnackbar(
          `Could not record consent. Confirm the ${subject} is connected to e-prescribing and try again.`,
          { variant: 'error' },
        );
      });
  }, [recordConsent, refetch, patientId, enqueueSnackbar, consentTracking, subject]);

  const handleFilterChanged = useCallback(
    (filter: 'all' | 'active' | 'inactive') => {
      tracking.filterChanged({ patient_id: patientId, filter });
    },
    [tracking, patientId],
  );

  const handleManageInDoseSpot = useCallback(
    (medicationId: string) => {
      tracking.manageRxClicked({ patient_id: patientId, medication_id: medicationId });
      window.open(prescribeHref, '_blank', 'noopener,noreferrer');
    },
    [patientId, prescribeHref, tracking],
  );

  const handleHistoryToggled = useCallback(
    (medicationId: string, expanded: boolean) => {
      tracking.historyToggled({
        patient_id: patientId,
        medication_id: medicationId,
        expanded,
      });
    },
    [tracking, patientId],
  );

  if (isLoading || isSpineLoading || !cardData) {
    return (
      <ClinicalCardFrame title="Medications">
        {isError ? (
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 4, textAlign: 'center' }}>
            Medications could not be loaded. Refresh to try again.
          </Typography>
        ) : (
          <Box sx={{ px: 2, pb: 2 }}>
            {[0, 1, 2].map((row) => (
              <Skeleton key={row} variant="rounded" height={44} sx={{ my: 1, borderRadius: 1 }} />
            ))}
          </Box>
        )}
      </ClinicalCardFrame>
    );
  }

  return (
    <MedicationsCard
      workspace={cardData.workspace}
      consentState={cardData.consentState}
      historyFills={cardData.historyFills}
      attributedFillCount={cardData.attributedFillCount}
      onAdd={handleAdd}
      onEdit={handleEdit}
      onManageInDoseSpot={handleManageInDoseSpot}
      onDiscontinue={handleDiscontinue}
      onRemove={handleRemove}
      onRecordConsent={handleRecordConsent}
      onFilterChanged={handleFilterChanged}
      onHistoryToggled={handleHistoryToggled}
      subject={subject}
    />
  );
};
